/**
 * The knowledge page: your articles, and the error signatures behind
 * "what this means".
 *
 * TWO TABS BECAUSE THEY ARE TWO DIFFERENT THINGS, and conflating them would
 * make both worse:
 *
 *   Articles are YOURS. Private to this organisation, written by your team,
 *   about your clients' arrangements and your way of doing things.
 *
 *   Signatures are the PRODUCT's. They map a vendor's error text to an
 *   explanation, ship with the software, and are identical for every tenant
 *   because they describe the vendor's behaviour rather than anyone's estate.
 *   An org can add its own, and those win where they overlap.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Article {
  id: string;
  title: string;
  body: string;
  tags?: string[];
  createdBy?: string;
  updatedBy?: string;
  updatedAt?: number;
  fromNote?: { label?: string; author?: string } | null;
}

interface Signature {
  id: string;
  match: string[];
  source: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  meaning: string;
  whatToDo: string;
}

const SOURCE_LABEL: Record<string, string> = {
  any: 'Any source', datto: 'Datto BCDR', sp: 'ShadowProtect', sc: 'SC Image Manager',
};

export function KnowledgeBase({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [tab, setTab] = useState<'articles' | 'signatures'>('articles');
  const [articles, setArticles] = useState<Article[]>([]);
  const [builtIn, setBuiltIn] = useState<Signature[]>([]);
  const [custom, setCustom] = useState<Signature[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<Article> | null>(null);
  const [editSig, setEditSig] = useState<Partial<Signature> | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [a, s] = await Promise.all([
        httpsCallable(functions, 'listArticles')({ orgId }),
        httpsCallable(functions, 'listSignatures')({ orgId }),
      ]);
      setArticles((a.data as { articles: Article[] }).articles ?? []);
      const sd = s.data as { builtIn: Signature[]; custom: Signature[] };
      setBuiltIn(sd.builtIn ?? []);
      setCustom(sd.custom ?? []);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not load.' });
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  if (!orgId) return null;

  async function run(fn: () => Promise<string>) {
    setBusy(true); setMsg(null);
    try { setMsg({ ok: true, text: await fn() }); await load(); }
    catch (err) { setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' }); }
    finally { setBusy(false); }
  }

  const q = search.trim().toLowerCase();
  const hit = (...parts: (string | undefined)[]) =>
    !q || parts.filter(Boolean).join(' ').toLowerCase().includes(q);

  const shownArticles = articles.filter((a) => hit(a.title, a.body, a.tags?.join(' ')));
  const shownSigs = [...custom, ...builtIn].filter((s) =>
    hit(s.title, s.meaning, s.whatToDo, s.match.join(' ')));
  const isCustom = (id: string) => custom.some((c) => c.id === id);

  return (
    <div className="settings">
      <section className="panel">
        <div className="kbp-head">
          <div className="kbp-tabs">
            <button className={'kbp-tab' + (tab === 'articles' ? ' active' : '')} onClick={() => setTab('articles')}>
              Articles <span className="dim">{articles.length}</span>
            </button>
            <button className={'kbp-tab' + (tab === 'signatures' ? ' active' : '')} onClick={() => setTab('signatures')}>
              Error signatures <span className="dim">{builtIn.length + custom.length}</span>
            </button>
          </div>
          <input
            className="field-input kbp-search" type="search" placeholder="Search knowledge..."
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

        {tab === 'articles' && (
          <>
            <p className="panel-note">
              Written by your team, private to this organisation. Nothing here is shared with another
              tenant. The assistant reads these when answering questions about your estate.
            </p>

            <button className="btn btn-primary" onClick={() => setEditing({ title: '', body: '' })}>
              New article
            </button>

            {editing && (
              <div className="kbp-edit">
                <label className="field">
                  <span className="field-label">Title</span>
                  <input
                    className="field-input" value={editing.title ?? ''}
                    placeholder="When Image Manager fills at a 727 site"
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Article</span>
                  <textarea
                    className="field-input" rows={8} value={editing.body ?? ''}
                    placeholder="What the situation is, what you do about it, and why."
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Tags, comma separated</span>
                  <input
                    className="field-input" value={(editing.tags ?? []).join(', ')}
                    placeholder="image-manager, disk-space"
                    onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(',').map((s) => s.trim()) })}
                  />
                </label>
                <div className="row-form">
                  <button
                    className="btn btn-primary" disabled={busy || !editing.title?.trim() || !editing.body?.trim()}
                    onClick={() => void run(async () => {
                      await httpsCallable(functions, 'upsertArticle')({ orgId, ...editing });
                      setEditing(null);
                      return 'Saved.';
                    })}
                  >
                    {busy ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            )}

            {shownArticles.length === 0 && !editing && (
              <div className="empty">
                {q ? 'Nothing matches that.' : 'No articles yet. Promote a note from a machine, or write one here.'}
              </div>
            )}

            {shownArticles.map((a) => (
              <article className="kbp-article" key={a.id}>
                <div className="kbp-article-head">
                  <h3>{a.title}</h3>
                  <div className="kbp-article-actions">
                    <button className="btn-link" onClick={() => setEditing(a)}>Edit</button>
                    <button
                      className="btn-link danger" disabled={busy}
                      onClick={() => {
                        if (!confirm(`Delete "${a.title}"?`)) return;
                        void run(async () => {
                          await httpsCallable(functions, 'deleteArticle')({ orgId, id: a.id });
                          return 'Deleted.';
                        });
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="kbp-article-body">{a.body}</div>
                <div className="kbp-article-meta">
                  {(a.tags ?? []).map((t) => <span className="tag" key={t}>{t}</span>)}
                  <span className="dim">
                    {a.createdBy}
                    {a.updatedAt ? ` · ${new Date(a.updatedAt).toLocaleDateString()}` : ''}
                  </span>
                  {/* Where it came from, so the original context is not lost. */}
                  {a.fromNote?.label && (
                    <span className="dim">promoted from a note on {a.fromNote.label}</span>
                  )}
                </div>
              </article>
            ))}
          </>
        )}

        {tab === 'signatures' && (
          <>
            <p className="panel-note">
              These turn a vendor's wording into an explanation. The built-in library ships with the
              product and is the same for everyone, because "not enough space on the disk" means the
              same thing everywhere. Your own signatures take precedence where they overlap.
            </p>

            {canManage && (
              <button
                className="btn btn-primary"
                onClick={() => setEditSig({ match: [], source: 'any', severity: 'warning' })}
              >
                New signature
              </button>
            )}

            {editSig && (
              <div className="kbp-edit">
                <label className="field">
                  <span className="field-label">Text to match on, one per line</span>
                  <textarea
                    className="field-input mono" rows={3}
                    placeholder={'not enough space on the disk\ndisk full'}
                    value={(editSig.match ?? []).join('\n')}
                    onChange={(e) => setEditSig({ ...editSig, match: e.target.value.split('\n') })}
                  />
                </label>
                <div className="row-form">
                  <label className="field">
                    <span className="field-label">Applies to</span>
                    <select
                      className="field-input" value={editSig.source ?? 'any'}
                      onChange={(e) => setEditSig({ ...editSig, source: e.target.value })}
                    >
                      {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field-label">Severity</span>
                    <select
                      className="field-input" value={editSig.severity ?? 'warning'}
                      onChange={(e) => setEditSig({ ...editSig, severity: e.target.value as Signature['severity'] })}
                    >
                      <option value="critical">Critical</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </select>
                  </label>
                </div>
                <label className="field">
                  <span className="field-label">Title</span>
                  <input
                    className="field-input" value={editSig.title ?? ''}
                    onChange={(e) => setEditSig({ ...editSig, title: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">What it means</span>
                  <textarea
                    className="field-input" rows={3} value={editSig.meaning ?? ''}
                    onChange={(e) => setEditSig({ ...editSig, meaning: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">What to do</span>
                  <textarea
                    className="field-input" rows={3} value={editSig.whatToDo ?? ''}
                    onChange={(e) => setEditSig({ ...editSig, whatToDo: e.target.value })}
                  />
                </label>
                <div className="row-form">
                  <button
                    className="btn btn-primary" disabled={busy || !editSig.title?.trim()}
                    onClick={() => void run(async () => {
                      await httpsCallable(functions, 'upsertSignature')({
                        orgId, ...editSig,
                        match: (editSig.match ?? []).map((s) => s.trim()).filter(Boolean),
                      });
                      setEditSig(null);
                      return 'Saved.';
                    })}
                  >
                    {busy ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn" onClick={() => setEditSig(null)}>Cancel</button>
                </div>
              </div>
            )}

            {shownSigs.map((s) => (
              <div className={'kb-sig kb-' + s.severity} key={s.id} style={{ marginTop: 10 }}>
                <div className="kbp-article-head">
                  <div className="kb-sig-title">
                    {s.title}
                    <span className="tag" style={{ marginLeft: 8 }}>{SOURCE_LABEL[s.source] ?? s.source}</span>
                    {isCustom(s.id) && <span className="tag tag-ok" style={{ marginLeft: 6 }}>yours</span>}
                  </div>
                  {canManage && isCustom(s.id) && (
                    <div className="kbp-article-actions">
                      <button className="btn-link" onClick={() => setEditSig(s)}>Edit</button>
                      <button
                        className="btn-link danger" disabled={busy}
                        onClick={() => void run(async () => {
                          await httpsCallable(functions, 'deleteSignature')({ orgId, id: s.id });
                          return 'Deleted.';
                        })}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                <div className="kb-sig-body">{s.meaning}</div>
                <div className="kb-sig-do"><strong>What to do:</strong> {s.whatToDo}</div>
                <div className="kbp-match">
                  matches: {s.match.map((m) => <code key={m}>{m}</code>)}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
